import {
  AfterViewInit,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
} from '@angular/core';
import { WebCashPermissionKeys } from '../../../../core/enums/WebCashPermissionKeys';
import { TooltipDirective } from '../../../../shared/directives/tooltip.directive';
import { PermissionService } from '../services/PermissionService';
import { IdentityStore } from '../stores/IdentityStore';

const interactiveControlSelector =
  'button:not([type="hidden"]),' +
  'a[href],' +
  'input:not([type="hidden"]),' +
  'select,' +
  'textarea,' +
  '[role="button"]';

/**
 * Disables the host control or the first matching descendant when the user lacks the WebCash
 * permission, or when `extraDisabled` is true. Composes `TooltipDirective` via `hostDirectives`.
 */
@Directive({
  selector: '[appWebCashPermissionControl]',
  standalone: true,
  hostDirectives: [
    {
      directive: TooltipDirective,
      inputs: ['placement', 'delay', 'maxWidth', 'mode'],
    },
  ],
})
export class WebCashPermissionControlDirective implements AfterViewInit {

  public readonly permissionKey = input<WebCashPermissionKeys | null>(null, {
    alias: 'appWebCashPermissionControl',
  });
  public readonly extraDisabled = input(false);
  public readonly permittedTooltipContent = input('');

  private readonly _tooltip = inject(TooltipDirective);
  private readonly _permissionService = inject(PermissionService);
  private readonly _identityStore = inject(IdentityStore);
  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _deniedTooltip =
    'You do not have permission to use this function.'.trans('common/noPermissionTooltip');

  public constructor() {
    this._tooltip.wrapHostForHover = true;

    effect(() => {
      this._identityStore.identity();
      this._identityStore.tmpIdentity();
      this.permissionKey();
      this.extraDisabled();
      this.permittedTooltipContent();
      this.apply();
    });
  }

  public ngAfterViewInit(): void {
    this.apply();
  }

  private apply(): void {
    const permission = this.permissionKey();
    let has: boolean;

    if (permission !== null) {
      has = this._permissionService.hasPermission(permission);
    } else {
      has = true;
    }

    if (!has) {
      this._tooltip.content = this._deniedTooltip;
    } else {
      this._tooltip.content = this.permittedTooltipContent();
    }

    const el = this.findInteractiveControl(this._host.nativeElement);

    if (!el) {
      return;
    }

    const disabled = !has || this.extraDisabled();

    if (
      el instanceof HTMLButtonElement ||
      el instanceof HTMLInputElement ||
      el instanceof HTMLSelectElement ||
      el instanceof HTMLTextAreaElement
    ) {
      el.disabled = disabled;
    }
  }

  /** Host if it matches, otherwise first matching descendant (DOM order). */
  private findInteractiveControl(root: HTMLElement): Element | null {
    if (root.matches(interactiveControlSelector)) {
      return root;
    }

    return root.querySelector(interactiveControlSelector);
  }
}
